// Edit-mode pagination for the document frame. CKEditor's free build has
// no print-style pagination, but the PageBreak plugin (loaded in
// TemplateEditor.ckeditor.tsx) renders explicit break widgets. This hook
// derives the page count from the controlled HTML value and steps the
// live editor DOM between those `.ck-page-break` markers — no model
// rebuild, no scraping of arbitrary document structure. It gives edit
// mode the same ‹ Prev / Page n of N / Next › control the read-only view
// already has (Rule 3 of the Document Frame design).
import { useCallback, useEffect, useMemo, useState } from "react";

// The slice of the CKEditor instance we touch to find the editable DOM.
// Kept structural so callers never couple to the concrete build.
export type PagerEditor = {
  editing?: { view?: { getDomRoot?: () => HTMLElement | null | undefined } };
  ui?: { getEditableElement?: () => HTMLElement | null | undefined };
};

// Count page breaks in serialized editor HTML. PageBreak emits
// `<div class="page-break" ...>`, so pages = breaks + 1 (never below 1).
export function countPages(html: string): number {
  const matches = html.match(/class="[^"]*\bpage-break\b[^"]*"/g);
  return (matches !== null ? matches.length : 0) + 1;
}

function editableRoot(editor: PagerEditor | null): HTMLElement | null {
  if (editor === null) return null;
  const fromView = editor.editing?.view?.getDomRoot?.();
  if (fromView !== null && fromView !== undefined) return fromView;
  return editor.ui?.getEditableElement?.() ?? null;
}

export interface EditorPager {
  /** 1-based current page. */
  page: number;
  total: number;
  prev: () => void;
  next: () => void;
}

export function useEditorPager(
  editor: PagerEditor | null,
  value: string
): EditorPager {
  const breakPages = useMemo(() => countPages(value), [value]);
  const [page, setPage] = useState(1);
  // Generated drafts carry no explicit breaks, so without this the footer
  // read "Page 1 of 1" on a long document. With no breaks, a page is one
  // viewport of the scrolling editable.
  const [viewPages, setViewPages] = useState(1);
  const hasBreaks = breakPages > 1;
  const total = hasBreaks ? breakPages : viewPages;

  useEffect(() => {
    const root = editableRoot(editor);
    if (root === null || hasBreaks) {
      setViewPages(1);
      return;
    }
    const measure = (): void => {
      const h = root.clientHeight;
      setViewPages(h > 0 ? Math.max(1, Math.ceil((root.scrollHeight - 1) / h)) : 1);
    };
    const onScroll = (): void => {
      const h = root.clientHeight;
      if (h > 0) setPage(Math.floor((root.scrollTop + h / 2) / h) + 1);
    };
    measure();
    root.addEventListener("scroll", onScroll);
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    ro?.observe(root);
    return () => {
      root.removeEventListener("scroll", onScroll);
      ro?.disconnect();
    };
  }, [editor, value, hasBreaks]);

  // Clamp when the document shrinks below the current page (breaks removed).
  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), total));
  }, [total]);

  const goTo = useCallback(
    (next: number): void => {
      const clamped = Math.min(Math.max(1, next), total);
      setPage(clamped);
      const root = editableRoot(editor);
      if (root === null) return;
      if (clamped <= 1) {
        root.scrollTop = 0;
        return;
      }
      if (!hasBreaks) {
        root.scrollTop = (clamped - 1) * root.clientHeight;
        return;
      }
      // page N (N>=2) begins just after break N-2 (page 2 → first break).
      const breaks = root.querySelectorAll(".ck-page-break");
      const target = breaks[clamped - 2];
      if (target instanceof HTMLElement) {
        target.scrollIntoView({ block: "start", behavior: "auto" });
      } else {
        root.scrollTop = 0;
      }
    },
    [editor, total, hasBreaks]
  );

  return {
    page,
    total,
    prev: () => goTo(page - 1),
    next: () => goTo(page + 1),
  };
}
